import { Family, Tier } from '../config';

/**
 * An ElementDef describes a unique element type in the game.
 * Each element has a family, a tier, a unique id, a display name, and lore.
 */
export interface ElementDef {
  id: string;           // e.g. "fire_fragment", "water_shard"
  family: Family;
  tier: Tier;
  name: string;         // Display name e.g. "Ember"
  lore: string;         // Short flavor text
  discovered: boolean;  // Whether player has found this (runtime state, not def)
}

/**
 * An ElementInstance is a placed element on the grid.
 */
export interface ElementInstance {
  uid: number;          // Unique runtime ID
  defId: string;        // References ElementDef.id
  col: number;
  row: number;
  age: number;          // Merge counter — how many merges have happened since this element was placed
  corrupted: boolean;   // If true, element is a blocker (unmergeable, takes up space)
}

// ===== ELEMENT DEFINITIONS (Starter Set — 56 elements: 8 families x 7 tiers) =====

function el(family: Family, tier: Tier, name: string, lore: string): ElementDef {
  const familyPrefix = family;
  const tierSuffix = Tier[tier].toLowerCase();
  return {
    id: `${familyPrefix}_${tierSuffix}`,
    family,
    tier,
    name,
    lore,
    discovered: false,
  };
}

export const ELEMENT_DEFS: ElementDef[] = [
  // ===== FIRE =====
  el(Family.Fire, Tier.Fragment, 'Spark',        'A tiny flicker of heat.'),
  el(Family.Fire, Tier.Shard,    'Ember',        'Warmth that refuses to die.'),
  el(Family.Fire, Tier.Crystal,  'Flame',        'Dancing tongues of orange light.'),
  el(Family.Fire, Tier.Gem,      'Blaze',        'An inferno compressed into form.'),
  el(Family.Fire, Tier.Core,     'Pyre',         'The heart of a great burning.'),
  el(Family.Fire, Tier.Primal,   'Inferno',      'All-consuming elemental fury.'),
  el(Family.Fire, Tier.Eternal,  'Solar Flare',  'The breath of a dying star.'),

  // ===== WATER =====
  el(Family.Water, Tier.Fragment, 'Droplet',      'A single bead of moisture.'),
  el(Family.Water, Tier.Shard,    'Ripple',       'Circles spreading outward.'),
  el(Family.Water, Tier.Crystal,  'Stream',       'Clear water finding its path.'),
  el(Family.Water, Tier.Gem,      'Torrent',      'Unstoppable rushing force.'),
  el(Family.Water, Tier.Core,     'Maelstrom',    'The spiraling deep.'),
  el(Family.Water, Tier.Primal,   'Tsunami',      'The ocean\'s wrath unleashed.'),
  el(Family.Water, Tier.Eternal,  'Abyssal Tide', 'Currents from the world\'s floor.'),

  // ===== EARTH =====
  el(Family.Earth, Tier.Fragment, 'Pebble',       'Worn smooth by ages.'),
  el(Family.Earth, Tier.Shard,    'Stone',        'Solid and unyielding.'),
  el(Family.Earth, Tier.Crystal,  'Boulder',      'Weight that commands respect.'),
  el(Family.Earth, Tier.Gem,      'Monolith',     'A pillar from deep below.'),
  el(Family.Earth, Tier.Core,     'Tectonic',     'The plates themselves shift.'),
  el(Family.Earth, Tier.Primal,   'Mountain',     'Ancient beyond reckoning.'),
  el(Family.Earth, Tier.Eternal,  'World Core',   'The molten heart of the planet.'),

  // ===== AIR =====
  el(Family.Air, Tier.Fragment, 'Breeze',       'A gentle whisper of movement.'),
  el(Family.Air, Tier.Shard,    'Gust',         'Enough to rustle leaves.'),
  el(Family.Air, Tier.Crystal,  'Wind',         'A steady invisible force.'),
  el(Family.Air, Tier.Gem,      'Gale',         'Howling through the gaps.'),
  el(Family.Air, Tier.Core,     'Tempest',      'The sky in furious motion.'),
  el(Family.Air, Tier.Primal,   'Hurricane',    'A wall of screaming air.'),
  el(Family.Air, Tier.Eternal,  'Void Wind',    'The breath between worlds.'),

  // ===== LIGHTNING =====
  el(Family.Lightning, Tier.Fragment, 'Static',      'A tingle on your skin.'),
  el(Family.Lightning, Tier.Shard,    'Spark',       'A bright crack in the air.'),
  el(Family.Lightning, Tier.Crystal,  'Arc',         'Electricity leaping between points.'),
  el(Family.Lightning, Tier.Gem,      'Bolt',        'Blinding speed and power.'),
  el(Family.Lightning, Tier.Core,     'Thunderstrike','The sky splits open.'),
  el(Family.Lightning, Tier.Primal,   'Storm Cell',  'A self-sustaining engine of fury.'),
  el(Family.Lightning, Tier.Eternal,  'Plasma Crown','Pure energy given form.'),

  // ===== SHADOW =====
  el(Family.Shadow, Tier.Fragment, 'Shade',       'A flicker at the edge of sight.'),
  el(Family.Shadow, Tier.Shard,    'Gloom',       'Light retreats from this place.'),
  el(Family.Shadow, Tier.Crystal,  'Dusk',        'The boundary between day and night.'),
  el(Family.Shadow, Tier.Gem,      'Eclipse',     'The sun hidden from the world.'),
  el(Family.Shadow, Tier.Core,     'Umbra',       'Total, consuming darkness.'),
  el(Family.Shadow, Tier.Primal,   'Void Shadow', 'Darkness that devours light.'),
  el(Family.Shadow, Tier.Eternal,  'Event Horizon','Where nothing escapes.'),

  // ===== LIGHT =====
  el(Family.Light, Tier.Fragment, 'Glimmer',     'The faintest suggestion of light.'),
  el(Family.Light, Tier.Shard,    'Gleam',       'A steady, warm glow.'),
  el(Family.Light, Tier.Crystal,  'Radiance',    'Bright enough to cast shadows.'),
  el(Family.Light, Tier.Gem,      'Brilliance',  'Eyes turn away instinctively.'),
  el(Family.Light, Tier.Core,     'Sunburst',    'Light compressed to its essence.'),
  el(Family.Light, Tier.Primal,   'Nova',        'A star reborn in an instant.'),
  el(Family.Light, Tier.Eternal,  'Cosmic Dawn', 'The first light of creation.'),

  // ===== VOID =====
  el(Family.Void, Tier.Fragment, 'Mote',        'A speck of nothing.'),
  el(Family.Void, Tier.Shard,    'Rift',        'A crack in the fabric of things.'),
  el(Family.Void, Tier.Crystal,  'Anomaly',     'Reality bends here.'),
  el(Family.Void, Tier.Gem,      'Singularity', 'A point of infinite density.'),
  el(Family.Void, Tier.Core,     'Null Core',   'Where physics breaks down.'),
  el(Family.Void, Tier.Primal,   'Oblivion',    'The erasure of all things.'),
  el(Family.Void, Tier.Eternal,  'The Unmaker',  'What existed before existence.'),

  // ===== WILD =====
  el(Family.Wild, Tier.Fragment, 'Prism Fragment', 'A shimmering shard that resonates with all elements.'),
];

// ===== LOOKUP HELPERS =====

const defMap = new Map<string, ElementDef>();
ELEMENT_DEFS.forEach(d => defMap.set(d.id, d));

export function getElementDef(id: string): ElementDef | undefined {
  return defMap.get(id);
}

export function getElementDefOrThrow(id: string): ElementDef {
  const def = defMap.get(id);
  if (!def) throw new Error(`Unknown element: ${id}`);
  return def;
}

/**
 * Given a family and tier, return the element def id.
 */
export function elementIdFor(family: Family, tier: Tier): string {
  return `${family}_${Tier[tier].toLowerCase()}`;
}

/**
 * Get the merge result: same family, tier + 1.
 * Returns undefined if already at max tier.
 */
export function getMergeResult(defId: string): string | undefined {
  const def = getElementDef(defId);
  if (!def) return undefined;
  const nextTier = def.tier + 1;
  if (nextTier > Tier.Eternal) return undefined;
  const resultId = elementIdFor(def.family, nextTier as Tier);
  return defMap.has(resultId) ? resultId : undefined;
}

/**
 * Get the splinter results: same family, tier - 1.
 * Returns undefined if below minimum splinter tier.
 */
export function getSplinterResult(defId: string): string | undefined {
  const def = getElementDef(defId);
  if (!def) return undefined;
  const prevTier = def.tier - 1;
  if (prevTier < Tier.Fragment) return undefined;
  const resultId = elementIdFor(def.family, prevTier as Tier);
  return defMap.has(resultId) ? resultId : undefined;
}

/**
 * Get a random fragment element (tier 1) for spawning.
 */
export function getRandomFragment(): ElementDef {
  const fragments = ELEMENT_DEFS.filter(d => d.tier === Tier.Fragment);
  return fragments[Math.floor(Math.random() * fragments.length)];
}

/**
 * Get a random fragment element (tier 1) for a specific family.
 * Falls back to getRandomFragment() if no fragment exists for the family.
 */
export function getRandomFragmentByFamily(family: Family): ElementDef {
  const fragments = ELEMENT_DEFS.filter(d => d.tier === Tier.Fragment && d.family === family);
  if (fragments.length === 0) return getRandomFragment();
  return fragments[Math.floor(Math.random() * fragments.length)];
}
